// Angular
import { NgModule } from '@angular/core';
import { Routes, RouterModule, UrlSegment, UrlMatchResult } from '@angular/router';
// Medsurf Lib
import { NavigationControlModels } from '@medsurf/flat-models';
import * as FlatGuards from '@medsurf/flat-guards';
import * as FlatResolver from '@medsurf/flat-resolvers';
// Components
import { ChaptersComponent } from './chapters/chapters.component';
import { ElearningsComponent } from './elearnings/elearnings.component';
import { ErrorsComponent } from './errors/errors.component';
import { LoginComponent } from './login/login.component';
import { PagesComponent } from './pages/pages.component';
import { SlideComponent } from './slide/slide.component';
import { SubjectsComponent } from './subjects/subjects.component';
import { RedirectControlComponent } from '@medsurf/flat-components';
import * as FlatModels from "@medsurf/flat-models";

/**
 * Navigation Matcher
 *
 * @param url
 */
const navigationMatcher = (url: UrlSegment[]): UrlMatchResult => {
  if (url.length <= 4) {
    const emptyUrlSegment = new UrlSegment(null, {});
    return {
      consumed: url,
      posParams: {
        eLearningSlug: url[0] || emptyUrlSegment,
        subjectSlug: url[1] || emptyUrlSegment,
        chapterSlug: url[2] || emptyUrlSegment,
        blockSlug: url[3] || emptyUrlSegment,
      }
    };
  }
  return null;
};

/**
 * App Routes
 */
const appRoutes: Routes = [
  {
    path: '',
    component: ElearningsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      footerLinks: [
        {title: 'DATENSCHUTZERKLÄRUNG', url: 'privacy'},
        {title: 'IMPRESSUM', url: 'impressum'}
      ],
    },
    resolve: {
      elearnings: FlatResolver.ElearningViewerResolver,
    },
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      title: 'sign_in',
    }
  },
  {
    path: '404',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      title: 'upps',
      errorType: 404
    }
  },
  {
    path: '401',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      title: 'upps',
      errorType: 401
    }
  },
  // Seite
  {
    path: 'seite/:pageSlug',
    component: PagesComponent,
    canActivate: [FlatGuards.NavigationControlGuard],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.PAGE,
      footerLinks: [
        {title: 'DATENSCHUTZERKLÄRUNG', url: 'privacy'},
        {title: 'IMPRESSUM', url: 'impressum'}
      ],
    },
    resolve: {
      nodes: FlatResolver.PageSharedResolver,
    },
  },
  // Node
  {
    path: 'node/:idSlug',
    component: RedirectControlComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.NODE_ID,
      title: 'slide',
      footerLinks: [
        {title: 'DATENSCHUTZERKLÄRUNG', url: 'privacy'},
        {title: 'IMPRESSUM', url: 'impressum'}
      ],
    },
    resolve: {
      nodes: FlatResolver.RedirectSharedResolver
    },
  },
  // Slide
  {
    path: 'slide/:idSlug',
    component: SlideComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      pathModelType: FlatModels.NavigationControlModels.PathModelType.enum.SLIDE_ID,
      title: 'slide',
      footerLinks: [
        {title: 'DATENSCHUTZERKLÄRUNG', url: 'privacy'},
        {title: 'IMPRESSUM', url: 'impressum'}
      ],
    },
    resolve: {
      nodes: FlatResolver.PageSharedResolver
    },
  },
  {
    path: ':eLearningSlug',
    component: SubjectsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.SLIDE,
      footerLinks: [
        {title: 'DATENSCHUTZERKLÄRUNG', url: 'privacy'},
        {title: 'IMPRESSUM', url: 'impressum'}
      ],
    },
    resolve: {
      nodes: FlatResolver.NodesByRouteSharedResolver
    },
  },
  {
    matcher: navigationMatcher,
    component: ChaptersComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.SLIDE,
    },
    resolve: {
      nodes: FlatResolver.NodesByRouteSharedResolver
    },
  },
  {
    // TODO deserialize old quiz route with new route
    path: 'quiz/:eLearningSlug/:subjectSlug/:chapterSlug/:blockSlug/:slideSlug',
    component: SlideComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
    canDeactivate: [
      FlatGuards.CanDeactivateViewerGuard
    ],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.TRAINING,
    },
    resolve: {
      slide: FlatResolver.NodesByRouteSharedResolver
    },
  },
  // TODO deserialize old quiz route -> load data and redirect to current new system
  {
    path: ':eLearningSlug/:subjectSlug/:chapterSlug/:blockSlug/:slideSlug',
    component: SlideComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard,
      FlatGuards.ResetQuizSharedGuard
    ],
    canDeactivate: [
      FlatGuards.CanDeactivateViewerGuard
    ],
    data: {
      pathModelType: NavigationControlModels.PathModelType.enum.SLIDE,
      orderedGuards: [
        FlatGuards.UserSharedGuard,
      ],
    },
    resolve: {
      slide: FlatResolver.NodesByRouteSharedResolver
    },
  },
  {
    path: '**',
    component: ErrorsComponent,
    canActivate: [
      FlatGuards.NavigationControlGuard,
      FlatGuards.UserViewerGuard
    ],
  }
];

/**
 * App Routing Module
 */
@NgModule({
  imports: [
    // Angular
    RouterModule.forRoot(appRoutes),
  ],
  exports: [
    // Angular
    RouterModule,
  ]
})
export class AppRoutingModule {
}
